/**
 * Teddly — "You may also like" product page recommendations.
 *
 * snippets/product-recommendations.liquid renders a strip that is already
 * ranked by the analytical signals Liquid can compute at page render: category
 * match, tag overlap, price band, merchandising flags, and the charm
 * cross-sell. Each card carries that score in `data-score`.
 *
 * This script adds the behavioural half. Shopify's own recommendation engine —
 * trained on this store's real order and browsing data — is queried for two
 * intents:
 *
 *   related        "shoppers who viewed this also viewed"
 *   complementary  curated / co-purchased add-ons
 *
 * Products it returns get a rank-decayed boost on top of their attribute score,
 * and anything it recommends that Liquid did not select is built into a card and
 * merged in. The strip is then re-sorted, capped so no single product type fills
 * it, and trimmed to the display limit.
 *
 * Everything here is progressive enhancement: if the fetch fails, the
 * server-rendered order stands unchanged.
 *
 * The React app runs the same model in src/lib/recommendations.ts — keep the two
 * in sync when tuning weights.
 */

(function () {
  'use strict';

  var WEIGHTS = {
    sameType: 40,
    priceAffinity: 20,
    accessoryCrossSell: 18,
    relatedTop: 35,
    relatedDecay: 3,
    relatedFloor: 8,
    complementaryTop: 30,
    complementaryDecay: 3,
    complementaryFloor: 6
  };

  var BAG_CHARMS = 'bag charms';

  function norm(s) {
    return (s || '').toString().trim().toLowerCase();
  }

  /** Rank-decayed boost for a product that appears in one of Shopify's lists. */
  function intentBoost(rank, top, decay, floor) {
    if (rank < 0) return 0;
    return Math.max(floor, top - rank * decay);
  }

  /**
   * Price affinity on a 0..1 scale — 1 at an identical price, decaying to 0 once
   * the candidate is twice (or half) the anchor.
   */
  function priceAffinity(anchorCents, candidateCents) {
    if (!anchorCents || !candidateCents) return 0;
    return Math.max(0, 1 - Math.abs(candidateCents - anchorCents) / anchorCents);
  }

  function formatMoney(cents) {
    var rupees = Math.round((cents || 0) / 100);
    return '₹' + rupees.toLocaleString('en-IN');
  }

  function sizedImage(url, width) {
    if (!url) return '';
    var abs = url.indexOf('//') === 0 ? 'https:' + url : url;
    return abs + (abs.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function fetchIntent(productId, intent) {
    var url =
      '/recommendations/products.json?product_id=' +
      encodeURIComponent(productId) +
      '&limit=10&intent=' +
      intent;
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.ok ? r.json() : { products: [] };
      })
      .then(function (data) {
        return (data && data.products) || [];
      })
      .catch(function () {
        return [];
      });
  }

  /** Build a card for a product Shopify recommended that Liquid did not render. */
  function buildCard(p, anchorType) {
    var variant = null;
    for (var i = 0; i < (p.variants || []).length; i++) {
      if (p.variants[i].available) {
        variant = p.variants[i];
        break;
      }
    }
    if (!variant) variant = (p.variants || [])[0];
    if (!variant) return null;

    var type = norm(p.type);
    var isAttach = anchorType !== BAG_CHARMS && type === BAG_CHARMS;
    var compareAt = variant.compare_at_price || 0;
    var price = variant.price || p.price || 0;
    var hasDiscount = compareAt > price;

    var card = document.createElement('div');
    card.className =
      'pdp-rec-card group flex flex-col bg-white rounded-xl overflow-hidden border border-[#e8ddd4] hover:shadow-md transition-all duration-300';
    card.dataset.productId = String(p.id);
    card.dataset.type = type;
    card.dataset.score = '0';

    var imgHtml = p.featured_image
      ? '<img src="' +
        sizedImage(p.featured_image, 400) +
        '" alt="" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" width="400" height="400">'
      : '';

    var attachBadge = isAttach
      ? '<span class="absolute top-2 left-2 bg-[#E8692E] text-white text-[9px] font-bold tracking-wide px-2 py-0.5 rounded-full">PAIRS WELL</span>'
      : '';

    var discountBadge = hasDiscount
      ? '<span class="absolute top-2 right-2 bg-green-600 text-white text-[9px] font-bold tracking-wide px-2 py-0.5 rounded-full">' +
        Math.round(((compareAt - price) / compareAt) * 100) +
        '% OFF</span>'
      : '';

    var comparePrice = hasDiscount
      ? '<span class="text-[11px] text-[#aaa] line-through">' + formatMoney(compareAt) + '</span>'
      : '';

    card.innerHTML =
      '<a href="' +
      p.url +
      '" class="relative block aspect-square bg-[#f0e8df] overflow-hidden">' +
      imgHtml +
      attachBadge +
      discountBadge +
      '</a>' +
      '<div class="p-2.5 flex flex-col gap-1 flex-1">' +
      '<a href="' +
      p.url +
      '" class="text-xs font-medium text-[#211a14] leading-tight min-h-[2rem]" ' +
      'style="display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden"></a>' +
      '<div class="flex items-baseline gap-1.5 flex-wrap">' +
      '<span class="text-sm font-bold text-[#E8692E]">' +
      formatMoney(price) +
      '</span>' +
      comparePrice +
      '</div>' +
      '<button type="button" class="add-to-cart-btn mt-auto w-full text-[11px] font-semibold py-1.5 rounded-full ' +
      'bg-[#E8692E] text-white hover:bg-[#d05a1f] active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed" ' +
      'data-variant-id="' +
      variant.id +
      '" data-available="' +
      !!variant.available +
      '"' +
      (variant.available ? '' : ' disabled') +
      '>+ Add</button>' +
      '</div>';

    // Titles go in as text, never markup — they are merchant-authored content.
    card.querySelectorAll('a')[1].textContent = p.title;
    var img = card.querySelector('img');
    if (img) img.alt = p.title;

    return card;
  }

  /** Attribute score for a card Shopify supplied but Liquid never scored. */
  function attributeScoreFor(p, anchorType, anchorPrice) {
    var score = 0;
    var type = norm(p.type);
    if (anchorType && type === anchorType) score += WEIGHTS.sameType;
    score += Math.round(priceAffinity(anchorPrice, p.price || 0) * WEIGHTS.priceAffinity);
    if (anchorType !== BAG_CHARMS && type === BAG_CHARMS) score += WEIGHTS.accessoryCrossSell;
    return score;
  }

  function trackImpression(section, cards) {
    if (typeof window.tcTrack !== 'function' || !window.IntersectionObserver) return;
    var fired = false;
    var observer = new IntersectionObserver(
      function (entries) {
        if (fired) return;
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          fired = true;
          observer.disconnect();
          window.tcTrack('view_item_list', {
            item_list_name: 'You may also like',
            items: cards.map(function (c, idx) {
              return { item_id: c.dataset.productId, index: idx };
            })
          });
          break;
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(section);
  }

  function init() {
    var section = document.getElementById('pdp-recs');
    if (!section) return;

    var grid = document.getElementById('pdp-recs-grid');
    if (!grid) return;

    var productId = section.dataset.productId;
    var anchorType = norm(section.dataset.productType);
    var anchorPrice = parseInt(section.dataset.productPrice, 10) || 0;
    var limit = parseInt(section.dataset.limit, 10) || 8;
    var maxPerType = parseInt(section.dataset.maxPerType, 10) || 3;

    if (!productId) return;

    Promise.all([
      fetchIntent(productId, 'related'),
      fetchIntent(productId, 'complementary')
    ]).then(function (results) {
      var related = results[0];
      var complementary = results[1];
      if (related.length === 0 && complementary.length === 0) {
        // Nothing behavioural to add — the server-rendered order already stands.
        trackImpression(section, Array.prototype.slice.call(grid.children));
        return;
      }

      var relatedRank = {};
      related.forEach(function (p, i) {
        relatedRank[String(p.id)] = i;
      });
      var complementaryRank = {};
      complementary.forEach(function (p, i) {
        complementaryRank[String(p.id)] = i;
      });

      var cards = Array.prototype.slice.call(grid.querySelectorAll('.pdp-rec-card'));
      var seen = {};
      cards.forEach(function (c) {
        seen[c.dataset.productId] = true;
      });

      // Merge in anything Shopify recommends that Liquid never selected.
      related.concat(complementary).forEach(function (p) {
        var id = String(p.id);
        if (seen[id] || id === String(productId) || !p.available) return;
        // Charms keep the component tag but are sold standalone — see the snippet.
        if ((p.tags || []).indexOf('rakhi-package-component') !== -1 && norm(p.type) !== BAG_CHARMS) return;
        var card = buildCard(p, anchorType);
        if (!card) return;
        card.dataset.score = String(attributeScoreFor(p, anchorType, anchorPrice));
        seen[id] = true;
        grid.appendChild(card);
        cards.push(card);
      });

      // Blend behavioural boosts onto the attribute scores.
      var ranked = cards.map(function (card) {
        var id = card.dataset.productId;
        var score = parseFloat(card.dataset.score) || 0;
        var r = relatedRank[id];
        if (r !== undefined) {
          score += intentBoost(r, WEIGHTS.relatedTop, WEIGHTS.relatedDecay, WEIGHTS.relatedFloor);
        }
        var c = complementaryRank[id];
        if (c !== undefined) {
          score += intentBoost(
            c,
            WEIGHTS.complementaryTop,
            WEIGHTS.complementaryDecay,
            WEIGHTS.complementaryFloor
          );
        }
        return { card: card, score: score, type: card.dataset.type || 'untyped' };
      });

      ranked.sort(function (a, b) {
        return b.score - a.score;
      });

      // Diversity pass — no more than maxPerType from any one category.
      var picked = [];
      var overflow = [];
      var perType = {};
      ranked.forEach(function (entry) {
        var used = perType[entry.type] || 0;
        if (picked.length < limit && used < maxPerType) {
          perType[entry.type] = used + 1;
          picked.push(entry);
        } else {
          overflow.push(entry);
        }
      });
      overflow.forEach(function (entry) {
        if (picked.length < limit) picked.push(entry);
        else entry.card.remove();
      });

      // Accessory slot — a charm is the easiest thing to add to an order, so
      // reserve one when the customer is looking at anything other than a charm.
      if (anchorType !== BAG_CHARMS && picked.length > 0) {
        var hasCharm = picked.some(function (e) {
          return e.type === BAG_CHARMS;
        });
        if (!hasCharm) {
          var topCharm = null;
          for (var i = 0; i < ranked.length; i++) {
            if (ranked[i].type === BAG_CHARMS) {
              topCharm = ranked[i];
              break;
            }
          }
          if (topCharm) {
            if (picked.length >= limit) picked.pop().card.remove();
            // The charm may have been trimmed out of the DOM as overflow above.
            if (!grid.contains(topCharm.card)) grid.appendChild(topCharm.card);
            picked.push(topCharm);
          }
        }
      }

      picked.forEach(function (entry, i) {
        entry.card.style.order = String(i);
        entry.card.style.display = '';
      });

      trackImpression(
        section,
        picked.map(function (e) {
          return e.card;
        })
      );
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
